import React, { createContext, useContext, useState, ReactNode } from 'react';

// Import the TabName enum
import { PatientInfoTabName } from '../patientDetailsView';

// Define the tab object type
type ActiveTabType = {
    name: PatientInfoTabName;
    reloadTabContent: boolean
};

// Define type for the setActiveTab parameter
type SetActiveTabParam = PatientInfoTabName | {
    name: PatientInfoTabName;
    reloadTabContent?: boolean  // Make reloadTabContent optional
};

interface ActiveTabContextType {
    activeTab: ActiveTabType;
    setActiveTab: (tab: SetActiveTabParam) => void;  // Accept either tab name or tab object
}

const ActiveTabContext = createContext<ActiveTabContextType | undefined>(undefined);

export const ActiveTabProvider: React.FC<{
    children: ReactNode;
    initialTab: PatientInfoTabName;
}> = ({ children, initialTab }) => {
    const [activeTab, setActiveTabState] = useState<ActiveTabType>({
        name: initialTab,
        reloadTabContent: false
    });

    // Wrapper for setState that handles different parameter types
    const setActiveTab = (tab: SetActiveTabParam) => {
        if (typeof tab === 'string') {
            // If a string is passed, use it as the tab name with default reloadTabContent=false
            setActiveTabState({
                name: tab,
                reloadTabContent: false
            });
        } else {
            // If an object is passed, use the provided reloadTabContent or default to false
            setActiveTabState({
                name: tab.name,
                reloadTabContent: tab.reloadTabContent ?? false
            });
        }
    };

    return (
        <ActiveTabContext.Provider value={{ activeTab, setActiveTab }}>
            {children}
        </ActiveTabContext.Provider>
    );
};

export const useActiveTab = (): ActiveTabContextType => {
    const context = useContext(ActiveTabContext);
    if (context === undefined) {
        throw new Error('useActiveTab must be used within an ActiveTabProvider');
    }
    return context;
};;